(() => {
  'use strict';

  const existingApi = window.VideoShoppingSlider;
  if (existingApi?.init) {
    existingApi.init(document);
    return;
  }

  const ROOT_SELECTOR = '.vs-section[data-section-id]';

  const loadSwiper = () => {
    if (window.Swiper) return Promise.resolve(window.Swiper);
    if (window.__videoShoppingSwiperPromise) return window.__videoShoppingSwiperPromise;

    window.__videoShoppingSwiperPromise = new Promise((resolve, reject) => {
      if (!document.querySelector('link[data-video-shopping-swiper]')) {
        const stylesheet = document.createElement('link');
        stylesheet.rel = 'stylesheet';
        stylesheet.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
        stylesheet.dataset.videoShoppingSwiper = 'true';
        document.head.appendChild(stylesheet);
      }

      const existingScript = document.querySelector('script[data-video-shopping-swiper]');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.Swiper), { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
      script.dataset.videoShoppingSwiper = 'true';
      script.onload = () => resolve(window.Swiper);
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return window.__videoShoppingSwiperPromise;
  };

  const initSection = (root) => {
    if (!root || root.dataset.vsReady === 'true') return;

    const slider = root.querySelector('[data-vs-slider]');
    const status = root.querySelector('[data-vs-status]');
    if (!slider || !root.querySelector('[data-vs-item]')) return;

    root.dataset.vsReady = 'true';

    const videoAutoplay = root.dataset.autoplay === 'true';
    const pauseOthers = root.dataset.pauseOthers === 'true';
    const drawer = root.querySelector('[data-vs-quick-drawer]');
    const drawerBody = root.querySelector('[data-vs-quick-body]');
    let quickShopTrigger = null;
    let previousBodyOverflow = '';

    const openQuickShop = (productId, trigger) => {
      const template = root.querySelector(`[data-vs-product-template="${String(productId)}"]`);
      if (!template || !drawer || !drawerBody) return;

      drawerBody.replaceChildren(template.content.cloneNode(true));
      quickShopTrigger = trigger;
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      root.classList.add('is-quick-open');
      drawer.setAttribute('aria-hidden', 'false');
      root.swiperInstance?.autoplay?.stop();
      window.setTimeout(() => root.querySelector('[data-vs-quick-close]')?.focus(), 40);
    };

    const closeQuickShop = (restoreFocus = true) => {
      if (!drawer) return;

      root.classList.remove('is-quick-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = previousBodyOverflow;
      if (root.dataset.carouselAutoplay === 'true') root.swiperInstance?.autoplay?.start();
      if (restoreFocus) quickShopTrigger?.focus();
      quickShopTrigger = null;
    };

    const collapseProductLists = (exceptCard = null) => {
      root.querySelectorAll('[data-vs-product-card]').forEach((card) => {
        if (card === exceptCard) return;
        card.classList.remove('is-expanded');
        card.querySelectorAll('[data-vs-extra-product]').forEach((row) => row.setAttribute('inert', ''));
      });

      root.querySelectorAll('[data-vs-products-toggle]').forEach((toggle) => {
        if (toggle.closest('[data-vs-product-card]') !== exceptCard) {
          toggle.setAttribute('aria-expanded', 'false');
        }
      });
    };

    const syncVideos = () => {
      root.querySelectorAll('[data-vs-item]').forEach((item) => {
        const video = item.querySelector('video');
        if (!video) return;

        if (item.classList.contains('swiper-slide-active')) {
          if (videoAutoplay) {
            video.muted = true;
            video.play().catch(() => {});
          }
        } else if (pauseOthers) {
          video.pause();
          video.muted = true;
          item.querySelector('[data-vs-sound]')?.setAttribute('aria-pressed', 'false');
        }
      });
    };

    root.addEventListener('click', (event) => {
      const productsToggle = event.target.closest('[data-vs-products-toggle]');
      if (productsToggle) {
        const productCard = productsToggle.closest('[data-vs-product-card]');
        const expanded = productsToggle.getAttribute('aria-expanded') !== 'true';
        collapseProductLists(productCard);
        productCard?.classList.toggle('is-expanded', expanded);
        productsToggle.setAttribute('aria-expanded', String(expanded));
        productCard?.querySelectorAll('[data-vs-extra-product]').forEach((row) => {
          if (expanded) row.removeAttribute('inert');
          else row.setAttribute('inert', '');
        });
        root.swiperInstance?.update();
        return;
      }

      const quickOpen = event.target.closest('[data-vs-quick-open]');
      if (quickOpen) {
        openQuickShop(quickOpen.dataset.productId, quickOpen);
        return;
      }

      if (event.target.closest('[data-vs-quick-close]') || event.target.closest('[data-vs-quick-overlay]')) {
        closeQuickShop();
        return;
      }

      const quantityButton = event.target.closest('[data-vs-qty-minus], [data-vs-qty-plus]');
      if (quantityButton) {
        const input = quantityButton.closest('.vs-quantity')?.querySelector('input[name="quantity"]');
        if (!input) return;
        const current = Math.max(1, Number(input.value) || 1);
        input.value = quantityButton.hasAttribute('data-vs-qty-plus')
          ? Math.min(99, current + 1)
          : Math.max(1, current - 1);
        return;
      }

      const soundButton = event.target.closest('[data-vs-sound]');
      if (!soundButton) return;

      const video = soundButton.closest('[data-vs-item]')?.querySelector('video');
      if (!video) return;
      const shouldUnmute = video.muted;

      if (shouldUnmute) {
        root.querySelectorAll('video').forEach((otherVideo) => {
          if (otherVideo !== video) otherVideo.muted = true;
        });
        root.querySelectorAll('[data-vs-sound]').forEach((button) => {
          if (button !== soundButton) button.setAttribute('aria-pressed', 'false');
        });
        video.play().catch(() => {});
      }

      video.muted = !shouldUnmute;
      soundButton.setAttribute('aria-pressed', String(shouldUnmute));
      soundButton.setAttribute(
        'aria-label',
        shouldUnmute ? root.dataset.muteLabel : root.dataset.unmuteLabel
      );
    });

    root.addEventListener('change', (event) => {
      const select = event.target.closest('[data-vs-variant-select]');
      if (!select) return;

      const option = select.options[select.selectedIndex];
      const quickProduct = select.closest('.vs-quick-product');
      const price = quickProduct?.querySelector('[data-vs-quick-price]');
      const image = quickProduct?.querySelector('.vs-quick-product__image');

      if (price) {
        price.textContent = '';
        if (option.dataset.comparePrice) {
          const compare = document.createElement('s');
          compare.textContent = option.dataset.comparePrice;
          price.append(compare, document.createTextNode(' '));
        }
        price.append(document.createTextNode(option.dataset.price || ''));
      }

      if (image && option.dataset.image) image.src = option.dataset.image;
    });

    root.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-vs-quick-form]');
      if (!form) return;

      event.preventDefault();
      const submit = form.querySelector('.vs-quick-submit');
      if (!submit || submit.disabled || submit.classList.contains('is-loading')) return;

      const formData = new FormData(form);
      const variantId = Number(formData.get('id'));
      const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
      const themeCart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      submit.classList.add('is-loading');
      submit.setAttribute('aria-disabled', 'true');

      try {
        const shopRoot = window.Shopify?.routes?.root || '/';
        const addPayload = { id: variantId, quantity };

        if (themeCart && typeof themeCart.getSectionsToRender === 'function') {
          addPayload.sections = themeCart.getSectionsToRender().map((section) => section.id);
          addPayload.sections_url = window.location.pathname;
          if (typeof themeCart.setActiveElement === 'function') themeCart.setActiveElement(submit);
        }

        const response = await fetch(window.routes?.cart_add_url || `${shopRoot}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(addPayload)
        });
        const addedData = await response.json();

        if (!response.ok || addedData.status) {
          throw new Error(
            addedData.description || addedData.message || root.dataset.addErrorMessage
          );
        }

        if (status) status.textContent = root.dataset.addedMessage;
        closeQuickShop(false);

        if (themeCart && addedData.sections && typeof themeCart.renderContents === 'function') {
          themeCart.renderContents(addedData);
        } else {
          const cartResponse = await fetch(`${shopRoot}cart.js`, {
            headers: { Accept: 'application/json' }
          });
          const cart = cartResponse.ok ? await cartResponse.json() : null;

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } })
          );
          document.dispatchEvent(
            new CustomEvent('cart:updated', { bubbles: true, detail: { cart } })
          );
          document.dispatchEvent(
            new CustomEvent('cart:open', { bubbles: true, detail: { cart } })
          );
          if (themeCart && typeof themeCart.open === 'function') themeCart.open();
        }
      } catch (error) {
        if (status) status.textContent = error.message || root.dataset.addErrorMessage;
      } finally {
        submit.classList.remove('is-loading');
        submit.removeAttribute('aria-disabled');
      }
    });

    root.vsCloseQuickShop = closeQuickShop;

    loadSwiper()
      .then((SwiperClass) => {
        if (!SwiperClass || !root.isConnected) return;

        const slideCount = root.querySelectorAll('[data-vs-item]').length;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const carouselAutoplay =
          root.dataset.carouselAutoplay === 'true' && !reduceMotion && slideCount > 1;

        root.swiperInstance = new SwiperClass(slider, {
          slidesPerView: 'auto',
          centeredSlides: true,
          spaceBetween: Number(root.dataset.mobileGap),
          speed: reduceMotion ? 0 : Number(root.dataset.transitionSpeed),
          loop: root.dataset.loop === 'true' && slideCount > 1,
          loopAdditionalSlides: 2,
          watchSlidesProgress: true,
          observer: true,
          observeParents: true,
          grabCursor: true,
          keyboard: { enabled: true },
          navigation: root.querySelector('[data-vs-prev]')
            ? {
                prevEl: root.querySelector('[data-vs-prev]'),
                nextEl: root.querySelector('[data-vs-next]')
              }
            : false,
          autoplay: carouselAutoplay
            ? {
                delay: Number(root.dataset.autoplayDelay),
                disableOnInteraction: false,
                pauseOnMouseEnter: root.dataset.pauseHover === 'true'
              }
            : false,
          breakpoints: {
            750: { spaceBetween: Number(root.dataset.desktopGap) }
          },
          on: {
            init: syncVideos,
            slideChangeTransitionStart: () => {
              syncVideos();
              collapseProductLists();
            },
            slideChangeTransitionEnd: syncVideos
          }
        });
      })
      .catch(() => root.classList.add('vs-swiper-failed'));
  };

  const init = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initSection(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initSection);
  };

  const destroy = (scope) => {
    const roots = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(ROOT_SELECTOR) || []);

    roots.forEach((root) => {
      if (root.classList.contains('is-quick-open')) root.vsCloseQuickShop?.(false);
      root.swiperInstance?.destroy?.(true, true);
      delete root.swiperInstance;
      delete root.vsCloseQuickShop;
      delete root.dataset.vsReady;
    });
  };

  window.VideoShoppingSlider = { init, destroy };

  init(document);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll(`${ROOT_SELECTOR}.is-quick-open`).forEach((root) => {
      root.vsCloseQuickShop?.();
    });
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
